import { fieldItemCount } from "./items.ts";
import type { Form, FormOffer, FormVisibility } from "./types.ts";

type WithSettings = Pick<Form, "settings">;

export const DEFAULT_SUBMIT_LABEL = "Submit";

export function submitLabelOf(form: WithSettings): string {
  return form.settings?.submitLabel?.trim() || DEFAULT_SUBMIT_LABEL;
}

export function formVisibility(form: WithSettings): FormVisibility {
  return form.settings?.visibility ?? "private";
}

export function submissionLimitOf(form: WithSettings): number | null {
  return form.settings?.submissionLimit ?? null;
}

export function isOncePerProject(form: WithSettings): boolean {
  return submissionLimitOf(form) === 1;
}

export function isSubmissionLimitReached(form: WithSettings, submitted: number): boolean {
  const limit = submissionLimitOf(form);
  return limit !== null && submitted >= limit;
}

export function offersResubmit(form: WithSettings, submitted = 0): boolean {
  const limit = submissionLimitOf(form);
  return form.settings?.resubmit === true && limit !== 1 && (limit === null || submitted < limit);
}

export type OfferTarget = { workflowId: string; issueTypeId: string | null };

export function isOfferedOn(form: WithSettings, project: OfferTarget): boolean {
  const offer = form.settings?.offeredOn;
  if (!offer) return true;
  return (
    offer.workflowIds.includes(project.workflowId) ||
    (project.issueTypeId !== null && offer.issueTypeIds.includes(project.issueTypeId))
  );
}

export function isOfferedAnywhere(form: WithSettings): boolean {
  const offer: FormOffer | undefined = form.settings?.offeredOn;
  return !offer || offer.workflowIds.length > 0 || offer.issueTypeIds.length > 0;
}

// A closed form still takes unfinished fillings to Submit; this gates new ones.
export function isFillableForm(form: Pick<Form, "status" | "items">): boolean {
  return form.status === "live" && fieldItemCount(form) > 0;
}
